import React, { useEffect, useMemo, useRef, useState } from 'react';
import { T, card as themeCard, button as btn } from './speedTheme';
import { evaluate, Mode, TOTEM_PCT } from './speedEngine';
import {
    BLANK_SLOT, Cap, MonsterBox, NumInput, PickerModal, SlotState, num,
    siegeSpeedLeader, siegeSkillHint, passiveOptionsOf
} from './speedShared';
import { collabTag } from '../utils/monster';

/**
 * **점령용** — 몹 셋을 고르고 **선턴 몹의 뒷속만** 넣으면, 뒤 몹들이 이어 치려면 뒷속이
 * 얼마나 있어야 하는지가 표로 나온다.
 *
 * 둥봇의 점령용 화면 그대로다. 사람이 넣는 건 선턴 몹의 뒷속(정보창의 +N)과 뒤 몹의
 * 공속아티 % 뿐이고, 나머지는 몹 표에서 읽는다:
 *   리더 %          맨 왼쪽 몹의 리더 스킬 — 점령전에서 먹는 공격속도 리더일 때만 (리더는 늘 첫 칸)
 *   게증·속업       선턴 몹의 스킬
 *   추가 속도       물호박(버프 수)·암헬하(패시브 +N)처럼 속도가 붙는 몹은 그 값을 고른다 — 자리와 상관없이 붙는다
 *
 * 결과 표의 **필요**가 답이다 — 그 몹이 그 차례에 이어 치려면 있어야 하는 뒷속. 셈은
 * `speedEngine.ts` 가 한다.
 */

const STORE = 'speedSiegePresets';

interface Saved {
    name: string;
    slots: SlotState[];
    /** 치는 차례 (칸 번호). 예전에 넣은 것에는 `leadIdx` 만 있다 */
    order?: number[];
    leadIdx?: number;
    /** 넣은 때 — 목록에 이것만 적는다 (예전에 넣은 것에는 없다) */
    at?: number;
}

/** 저장된 조합의 차례 — 예전 것에는 `leadIdx` 만 있어서 거기서 지어낸다 */
function orderOf(p: Saved): number[] {
    if (Array.isArray(p.order) && p.order.length === 3) return p.order;
    const lead = p.leadIdx ?? 0;
    return [lead, ...[0, 1, 2].filter((i) => i !== lead)];
}

/** 넣은 때 — 2026.09.11 01:24 */
function whenText(at?: number): string {
    if (!at) return '';
    const d = new Date(at);
    const two = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}.${two(d.getMonth() + 1)}.${two(d.getDate())} ${two(d.getHours())}:${two(d.getMinutes())}`;
}

function loadSaved(): Saved[] {
    try {
        const v = JSON.parse(localStorage.getItem(STORE) || '[]');
        return Array.isArray(v) ? v.filter((p: any) => Array.isArray(p?.slots)).slice(0, 30) : [];
    } catch {
        return [];
    }
}

function storeSaved(list: Saved[]) {
    try {
        localStorage.setItem(STORE, JSON.stringify(list.slice(0, 30)));
    } catch {
        /* 사생활 보호 모드에서는 막힌다 — 저장만 안 될 뿐이다 */
    }
}

export default function SpeedSiege({ isMobile, monsterMapping }: { isMobile: boolean; monsterMapping: any }) {
    /** 그림 경로에 짝 표를 붙인다 — `getMonsterImgPath`·`buildCells` 와 같은 규칙 */
    const taggedPath = (p: string, family: string): string => {
        const mate = monsterMapping?.monsters?.[family]?.collab;
        return p && mate ? `${p}?c=${collabTag(String(mate))}` : p;
    };
    const [slots, setSlots] = useState<SlotState[]>([{ ...BLANK_SLOT }, { ...BLANK_SLOT }, { ...BLANK_SLOT }]);
    /**
     * **치는 차례** — 칸 번호를 차례대로 담는다. 맨 앞이 선턴이다.
     * 리더는 이것과 상관없이 늘 첫 칸(왼쪽 상자)이다.
     */
    const [order, setOrder] = useState<number[]>([0, 1, 2]);
    /** 순서 지정 중이면 지금까지 찍은 칸들. null 이면 평소 (누르면 몹 고르기) */
    const [picking, setPicking] = useState<number[] | null>(null);
    /** 선턴 몹의 뒷속 */
    const [leadFlat, setLeadFlat] = useState('');
    /** 패시브로 붙는 추가 속도 (물호박·암헬하) — `칸:몹이름` 마다 고른 칸 번호. 몹을 바꾸면 열쇠가 달라져 기본값(0)으로 돌아간다 */
    const [bonusPick, setBonusPick] = useState<Record<string, number>>({});
    const [picker, setPicker] = useState<number | null>(null);
    const [saved, setSaved] = useState<Saved[]>([]);
    /** 즐겨찾기 이름 — 몹 이름을 이어 붙인다 (선턴 자리까지 같아야 같은 조합) */
    const favName = useMemo(() => slots.map((s) => s.name || '?').join('+') + ' [' + order.map((i) => i + 1).join('') + ']', [slots, order]);
    const isFav = saved.some((p) => p.name === favName);
    const toggleFav = () => {
        if (!slots.some((s) => s.name)) return;
        const next = isFav ? saved.filter((p) => p.name !== favName) : [{ name: favName, slots, order, at: Date.now() }, ...saved];
        setSaved(next); storeSaved(next);
    };
    const [showTable, setShowTable] = useState(false);

    useEffect(() => { setSaved(loadSaved()); }, []);

    const set = (i: number, patch: Partial<SlotState>) =>
        setSlots((prev) => prev.map((s, k) => (k === i ? { ...s, ...patch } : s)));

    const leadIdx = order[0];
    const lead = slots[leadIdx];

    /** 순서 지정 — 단추를 누르면 시작하고, 상자를 1·2·3 으로 찍으면 끝난다 */
    const startPicking = () => setPicking([]);
    const pickTurn = (i: number) => {
        if (!picking || picking.includes(i)) return;
        const next = [...picking, i];
        // 셋을 다 찍으면 그것이 차례가 되고 지정은 끝난다
        if (next.length === 3) { setOrder(next); setPicking(null); } else setPicking(next);
    };
    /** 상자에 붙는 차례 번호 — 지정 중이면 지금 찍은 것까지만 보여 준다 */
    const turnOf = (i: number): string | undefined => {
        const from = picking ?? order;
        const k = from.indexOf(i);
        return k >= 0 ? String(k + 1) : undefined;
    };

    /** 리더 % — 첫 칸 몹의 리더 스킬이 점령전에서 먹는 공격속도 리더일 때만 */
    const leaderPct = useMemo(
        () => siegeSpeedLeader(slots[0].leader, slots.slice(1).map((s) => s.attribute)) ?? 0,
        [slots]
    );

    /** 게증·속업 — 선턴 몹의 스킬 */
    const hint = useMemo(() => siegeSkillHint(lead.skills || []), [lead]);
    const mode: Mode = hint.mode || 'gauge';

    /** 칸마다 패시브로 붙는 추가 속도 (물호박·암헬하). 없는 몹이면 0 */
    const pickOf = (i: number) => bonusPick[i + ':' + slots[i].name] || 0;
    const bonuses = slots.map((s, i) => passiveOptionsOf(s.name)?.options[pickOf(i)]?.bonus || 0);
    const bonus = bonuses[leadIdx];

    /**
     * **셋이 다 차는 순간 순서 지정을 연다.**
     *
     * 몹만 골라 놓고 차례를 안 정하면 왼쪽부터라는 엉뚱한 차례로 판정이 나온다. 그래서
     * 다 채운 그 순간에 한 번 열어 준다 — 비었다가 다시 찰 때만이라, 다 찍고 난 뒤에는
     * 다시 열리지 않는다.
     */
    const wasFull = useRef(false);
    useEffect(() => {
        const full = slots.every((s) => !!s.name);
        if (full && !wasFull.current) setPicking([]);
        wasFull.current = full;
    }, [slots]);

    /** 다음에 고를 칸 — 전적검색처럼 여기만 깜박인다 */
    const nextEmpty = slots.findIndex((s) => !s.name);

    /**
     * 셋을 다 골랐고 차례까지 정했는가 — 그 전에는 입력·결과를 아예 안 띄운다.
     * 차례를 찍는 도중에 판정이 보이면 아직 안 정한 차례로 셈한 숫자를 읽게 된다.
     */
    const ready = picking === null && slots.every((s) => !!s.name && num(s.base) > 0);

    const result = useMemo(() => {
        if (num(lead.base) <= 0 || leadFlat === '') return null;
        const party = order.filter((i, k) => k === 0 || num(slots[i].base) > 0);
        if (party.length < 2) return null;
        return evaluate(
            party.map((i, k) => ({ name: slots[i].name, base: num(slots[i].base), flat: k === 0 ? num(leadFlat) : 0, artiPct: num(slots[i].arti), bonus: bonuses[i] })),
            { leaderPct, gaugePct: hint.gauge, mode }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slots, order, lead, leadFlat, bonuses.join(), leaderPct, hint, mode]);

    /** 표 한 줄씩 — 순서·몹·기본속·필요·최종 */
    const rows = useMemo(() => {
        if (!result) return [];
        const mult = 1 + (leaderPct + TOTEM_PCT) / 100;
        const head = { turn: 1, slot: lead, base: num(lead.base), need: num(leadFlat), final: Math.ceil(result.leadExact) };
        const rest = result.followers.map((f, k) => ({
            turn: k + 2, slot: slots[order[k + 1]], base: f.base, need: f.needFlat, final: Math.ceil(f.base * mult + f.needFlat + bonuses[order[k + 1]])
        }));
        return [head, ...rest];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result, lead, leadFlat, slots, order, leaderPct, bonuses.join()]);


    const font = isMobile ? 13 : 14;
    const card: React.CSSProperties = { ...themeCard, padding: isMobile ? 12 : 16 };
    const chip = (label: string, value: React.ReactNode): React.ReactNode => (
        <span style={{
            display: 'inline-flex', alignItems: 'baseline', gap: 8, padding: '6px 14px', borderRadius: 999,
            border: '1px solid rgba(25,118,210,0.35)', background: 'rgba(25,118,210,0.06)', fontSize: font
        }}>
            <span style={{ color: T.blue, fontWeight: 700 }}>{label}</span>
            <span style={{ fontWeight: 800, fontSize: font + 4, color: T.blue, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        </span>
    );
    const thumb = (s: SlotState, size = 18): React.ReactNode => s.imgPath
        ? <img loading="lazy" src={'/mobs/v2/' + s.imgPath.replace(/\\/g, '/')} alt="" style={{ width: size, height: size, borderRadius: 4, verticalAlign: 'middle', marginRight: 4 }} />
        : null;

    return (
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
            {/* ── 몬스터 선택 ── */}
            <div style={{ ...card, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, flexWrap: 'nowrap' }}>
                    <span style={{ fontWeight: 800, fontSize: font + 1, color: T.gold, whiteSpace: 'nowrap' }}>몬스터 선택</span>
                    {/* 순서 지정 — 누른 뒤 상자를 1·2·3 으로 찍는다. 첫 번째가 선턴이다 */}
                    <button
                        onClick={() => (picking ? setPicking(null) : startPicking())}
                        title="누른 뒤 몹 상자를 치는 차례대로 누르세요"
                        style={{
                            marginLeft: 'auto', border: '1px solid ' + T.blue, borderRadius: 999,
                            padding: isMobile ? '6px 10px' : '6px 14px', whiteSpace: 'nowrap',
                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            background: picking ? T.blue : 'rgba(25,118,210,0.06)', color: picking ? '#fff' : T.blue,
                            display: 'inline-flex', alignItems: 'center', gap: 6
                        }}
                    >
                        <span style={{ fontSize: 14, lineHeight: 1 }}>⟳</span>
                        {picking ? '순서 지정 중' : '순서 지정'}
                    </button>
                    <button
                        onClick={() => { setSlots([{ ...BLANK_SLOT }, { ...BLANK_SLOT }, { ...BLANK_SLOT }]); setOrder([0, 1, 2]); setPicking(null); setLeadFlat(''); }}
                        style={{ ...btn(true, '#78909c'), whiteSpace: 'nowrap', padding: isMobile ? '7px 10px' : '7px 14px' }}
                    >초기화</button>
                    {/* 즐겨찾기 — 옆 단추들과 키를 맞춘 네모 칸 */}
                    <button
                        onClick={toggleFav}
                        disabled={!slots.some((s) => s.name)}
                        title={isFav ? '즐겨찾기에서 빼기' : '즐겨찾기에 넣기'}
                        style={{
                            border: '1px solid ' + (isFav ? '#f5b301' : '#cfd8dc'),
                            background: isFav ? 'rgba(245, 179, 1, 0.10)' : 'transparent',
                            borderRadius: 8, padding: '4px 10px', lineHeight: 1, fontSize: 18,
                            cursor: slots.some((s) => s.name) ? 'pointer' : 'not-allowed',
                            color: isFav ? '#f5b301' : '#c5cad1',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 30
                        }}
                    >{isFav ? '★' : '☆'}</button>
                </div>

                {/*
                  * 순서를 찍는 동안에는 무엇을 해야 하는지 크게 적어 둔다. 단추 글자만으로는
                  * 화면이 바뀐 줄 모르고 몹을 다시 고르려다 차례가 정해져 버렸다.
                  */}
                {picking && (
                    <div
                        className="pick-slot-pulse"
                        style={{
                            marginTop: 10, padding: '10px 12px', borderRadius: 12,
                            border: '2px solid ' + T.blue, background: 'rgba(25,118,210,0.08)',
                            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'
                        }}
                    >
                        <span style={{ fontSize: font, fontWeight: 800, color: T.blue }}>
                            턴 순서대로 몬스터를 눌러주세요
                        </span>
                    </div>
                )}

                {/* 리더 효과와 몹 상자 — 둘 다 가운데 */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ margin: isMobile ? '16px 0 10px' : '8px 0 10px' }}>
                    <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 999, border: '1px solid rgba(25,118,210,0.35)', color: T.blue, fontWeight: 700 }}>
                        리더 {leaderPct}%
                    </span>
                </div>

                <div style={{ display: 'flex', gap: isMobile ? 'clamp(8px, 1.5vw, 9px)' : 6 }}>
                    {slots.map((s, i) => (
                        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: isMobile ? 'clamp(56px, 11vw, 60px)' : 92 }}>
                            <MonsterBox
                                imgPath={s.imgPath}
                                name={s.name}
                                badge={turnOf(i)}
                                compact={isMobile}
                                pulse={picking ? !picking.includes(i) : i === nextEmpty}
                                onClick={() => (picking ? pickTurn(i) : setPicker(i))}
                            />
                            {/* 이름 — 둥봇처럼 상자 아래에. 빈 칸은 비워 둔다 */}
                            <div style={{ fontSize: 11, color: T.text, fontWeight: 600, height: 14, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {s.name}
                            </div>
                        </div>
                    ))}
                </div>
                </div>
                </div>
            </div>

            {/* ── 입력 — 셋을 다 골라야 나온다 ── */}
            {ready && (
            <div style={{ ...card, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, fontSize: font + 1, color: T.gold }}>입력</span>
                    {/* 선턴 몹이 주는 것 — 둥봇의 오른쪽 위 칩 */}
                    <span style={{ marginLeft: 'auto', fontSize: 12, padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(25,118,210,0.35)', background: 'rgba(25,118,210,0.06)', color: T.blue, fontWeight: 700 }}>
                        {thumb(lead)}
                        {hint.gauge > 0 && <span>게증 {hint.gauge}% </span>}
                        {hint.speedUp && <span>속업 </span>}
                        {bonus > 0 && <span>+{bonus}</span>}
                        {!hint.gauge && !hint.speedUp && !bonus && <span style={{ color: T.sub, fontWeight: 500 }}>{lead.name ? '버프 없음' : '선턴 몹'}</span>}
                    </span>
                </div>

                <Cap>{lead.name || '선턴 몹'} 뒷속</Cap>
                <NumInput value={leadFlat} onChange={setLeadFlat} font={font + 4} placeholder="0" />

                {/* 패시브로 속도가 붙는 몹 — 치는 차례대로, 그런 몹만 */}
                {order.map((i) => {
                    const ps = passiveOptionsOf(slots[i].name);
                    if (!ps) return null;
                    const on = pickOf(i);
                    return (
                        <div key={i} style={{ marginTop: 8 }}>
                            <Cap>{thumb(slots[i])}{slots[i].name} 추가 속도 ({ps.title})</Cap>
                            <div style={{ display: 'flex', gap: 6 }}>
                                {ps.options.map((o, k) => (
                                    <button key={k} onClick={() => setBonusPick((p) => ({ ...p, [i + ':' + slots[i].name]: k }))} style={{
                                        flex: 1, border: '1px solid ' + (k === on ? T.blue : '#dfe3e8'), borderRadius: 8, padding: '6px 0',
                                        fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                        background: k === on ? T.blue : 'transparent', color: k === on ? '#fff' : '#888'
                                    }}>{o.label} {o.bonus ? '+' + o.bonus : ''}</button>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {/* 공속아티는 속업을 키우는 값 — 선턴 몹이 속업을 줄 때만 칸을 둔다 */}
                {hint.speedUp && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                        {order.slice(1).map((i) => (
                            <div key={i}>
                                <Cap>{thumb(slots[i])}{slots[i].name || (i + 1) + '번'} 공속아티 %</Cap>
                                <NumInput value={slots[i].arti} onChange={(v) => set(i, { arti: v })} max={2} font={font} placeholder="0" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            )}

            {/* ── 결과 — 선턴 뒷속까지 넣어야 나온다 ── */}
            {ready && result && (
            <div style={{ ...card, marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, fontSize: font + 1, color: T.gold }}>결과</span>
                    {chip('선턴 총공속', Math.round(result.leadExact))}
                    {chip('틱 수', result.tick)}
                    <button onClick={() => setShowTable(!showTable)} style={{ ...btn(true), marginLeft: 'auto' }}>공속계산표</button>
                </div>

                {result && (
                    <div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: font, textAlign: 'center' }}>
                                <thead>
                                    <tr style={{ background: 'rgba(102, 126, 234, 0.06)' }}>
                                        {['순서', '몬스터', '기본속', '필요', '최종'].map((h) => (
                                            <th key={h} style={{
                                                padding: '10px 8px', fontWeight: 800, border: '1px solid rgba(102,126,234,0.15)',
                                                background: h === '필요' ? T.blue : undefined, color: h === '필요' ? '#fff' : T.text
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.turn}>
                                            <td style={{ padding: '10px 8px', border: '1px solid rgba(102,126,234,0.15)' }}>{r.turn}</td>
                                            <td style={{ padding: '10px 8px', border: '1px solid rgba(102,126,234,0.15)', textAlign: 'left' }}>{thumb(r.slot, 22)}{r.slot.name || '?'}</td>
                                            <td style={{ padding: '10px 8px', border: '1px solid rgba(102,126,234,0.15)' }}>{r.base}</td>
                                            <td style={{ padding: '10px 8px', border: '1px solid rgba(102,126,234,0.15)', background: 'rgba(25,118,210,0.08)', color: T.blue, fontWeight: 800, fontSize: font + 2 }}>{r.need}</td>
                                            <td style={{ padding: '10px 8px', border: '1px solid rgba(102,126,234,0.15)' }}>{r.final}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    </div>
                )}

                {result && showTable && (
                    <div style={{ ...card, marginBottom: 12 }}>
                        <div style={{ fontWeight: 800, fontSize: font + 1, marginBottom: 8, color: T.gold }}>공속계산표</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: font, textAlign: 'center' }}>
                                <thead><tr style={{ background: 'rgba(102, 126, 234, 0.06)' }}>
                                    <th style={{ padding: 8, border: '1px solid rgba(102,126,234,0.15)' }}>선턴 뒷속</th>
                                    <th style={{ padding: 8, border: '1px solid rgba(102,126,234,0.15)' }}>총공속</th>
                                    <th style={{ padding: 8, border: '1px solid rgba(102,126,234,0.15)' }}>틱</th>
                                    {order.slice(1).map((i) => <th key={i} style={{ padding: 8, border: '1px solid rgba(102,126,234,0.15)' }}>{slots[i].name || (i + 1) + '번'} 필요</th>)}
                                </tr></thead>
                                <tbody>
                                    {[-40, -30, -20, -10, 0, 10, 20, 30, 40].map((d) => {
                                        const f = num(leadFlat) + d;
                                        if (f < 0) return null;
                                        const party = order.filter((i, k) => k === 0 || num(slots[i].base) > 0)
                                            .map((i, k) => ({ name: slots[i].name, base: num(slots[i].base), flat: k === 0 ? f : 0, artiPct: num(slots[i].arti), bonus: bonuses[i] }));
                                        const r = evaluate(party, { leaderPct, gaugePct: hint.gauge, mode });
                                        if (!r) return null;
                                        return (
                                            <tr key={d} style={{ background: d === 0 ? 'rgba(25,118,210,0.08)' : undefined, fontWeight: d === 0 ? 800 : 500 }}>
                                                <td style={{ padding: 8, border: '1px solid rgba(102,126,234,0.15)' }}>{f}</td>
                                                <td style={{ padding: 8, border: '1px solid rgba(102,126,234,0.15)' }}>{Math.round(r.leadExact)}</td>
                                                <td style={{ padding: 8, border: '1px solid rgba(102,126,234,0.15)' }}>{r.tick}</td>
                                                {r.followers.map((x, k) => <td key={k} style={{ padding: 8, border: '1px solid rgba(102,126,234,0.15)', color: T.blue }}>{x.needFlat}</td>)}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
            )}

            {/* ── 즐겨찾기 — 맨 아래. 누르면 그 조합이 그대로 들어온다 ── */}
            {saved.length > 0 && (
                <div style={card}>
                    {/* 머리글 — 위 칸들(몬스터 선택·입력·결과)과 같은 줄, 같은 글자 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, fontSize: font + 1, color: T.gold }}>즐겨찾기</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {saved.map((p) => {
                            const on = p.name === favName;
                            return (
                                <div
                                    key={p.name}
                                    /* 즐겨찾기에는 차례가 들어 있다 — 다시 찍게 하지 않는다 */
                                    onClick={() => { setSlots(p.slots.map((s) => ({ ...BLANK_SLOT, ...s }))); setOrder(orderOf(p)); setPicking(null); wasFull.current = true; }}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 10, cursor: 'pointer',
                                        border: '1px solid ' + (on ? T.blue : 'rgba(102,126,234,0.15)'), background: on ? 'rgba(25,118,210,0.06)' : 'transparent'
                                    }}
                                >
                                    {p.slots.map((s, k) => (
                                        <span key={k} style={{ position: 'relative', display: 'inline-flex' }}>
                                            {thumb({ ...BLANK_SLOT, ...s }, 30)}
                                            <span style={{ position: 'absolute', right: 2, bottom: -2, fontSize: 9, fontWeight: 800, color: '#fff', background: '#1976d2', borderRadius: 4, padding: '0 3px' }}>
                                                {orderOf(p).indexOf(k) + 1}
                                            </span>
                                        </span>
                                    ))}
                                    {/* 몹 이름은 그림으로 알아본다 — 여기에는 넣은 때만 적는다 */}
                                    <span style={{ flex: 1 }} />
                                    <span style={{ fontSize: 11, fontWeight: 600, color: T.sub, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                        {whenText(p.at)}
                                    </span>
                                    <span
                                        onClick={(e) => { e.stopPropagation(); const next = saved.filter((x) => x.name !== p.name); setSaved(next); storeSaved(next); }}
                                        style={{ color: T.red, fontWeight: 800, padding: '0 4px' }}
                                        title="지우기"
                                    >×</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {picker !== null && (
                <PickerModal
                    isMobile={isMobile}
                    monsterMapping={monsterMapping}
                    onClose={() => setPicker(null)}
                    onPick={(name, base, imgPath, attribute, leaderSkill, skills) =>
                        // 짝 표를 붙여 다른 화면과 **같은 주소**가 되게 한다 — 안 그러면 같은 몹을 두 벌 받는다
                        set(picker, {
                            name,
                            imgPath: taggedPath(imgPath, name),
                            attribute, leader: leaderSkill, skills,
                            base: base === null ? '' : String(base)
                        })}
                />
            )}
        </div>
    );
}
